import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import ProtectedRoute from './components/ProtectedRoute';
import { checkAuth } from './redux/authSlice';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import AcceptTeamInvitePage from './pages/AcceptTeamInvitePage';
import PublicFormPage from './pages/forms/PublicFormPage';
import FormsListPage from './pages/forms/FormsListPage';
import FormBuilderPage from './pages/forms/FormBuilderPage';
import FormSubmissionsPage from './pages/forms/FormSubmissionsPage';
import RegisterPage from './pages/RegisterPage';
import DashboardLayout from './pages/DashboardLayout';
import SubscriptionPage from './pages/SubscriptionPage';
import PaymentStatusPage from './pages/PaymentStatusPage';
import InvoicesPage from './pages/InvoicesPage';
import AdminCoupons from './pages/AdminCoupons';
import SettingsPage from './pages/SettingsPage';
import ApiDocsPage from './pages/ApiDocsPage';
import TeamManagementPage from './pages/admin/TeamManagementPage';
import DepartmentsPage from './pages/admin/DepartmentsPage';
import LegalPage from './pages/LegalPage';
import SupportPage from './pages/SupportPage';
import ContactPage from './pages/ContactPage';
import WebSupportPage from './pages/WebSupportPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';

// AI-MOS Pages (Hidden - Under Development)
// import AiMosDashboard from './pages/ai-mos/AiMosDashboard';
// import RecommendationsPage from './pages/ai-mos/RecommendationsPage';
// import LeadIntelligence from './pages/ai-mos/LeadIntelligence';
// import AlertsCenter from './pages/ai-mos/AlertsCenter';
// import ReportBuilderPage from './pages/ai-mos/ReportBuilderPage';
// import AutomationPage from './pages/ai-mos/AutomationPage';

// CRM / RevOps Pages
import ContactsPage from './pages/crm/ContactsPage';
import AccountsPage from './pages/crm/AccountsPage';
import AccountDetailPage from './pages/crm/AccountDetailPage';
import ContactDetailPage from './pages/crm/ContactDetailPage';
import LeadDetailPage from './pages/crm/LeadDetailPage';
import DealDetailPage from './pages/crm/DealDetailPage';
import OpportunitiesPage from './pages/crm/OpportunitiesPage';
import EventsPage from './pages/crm/EventsPage';
import LeadsPage from './pages/crm/LeadsPage';
import DealsPage from './pages/crm/DealsPage';
import TasksPage from './pages/crm/TasksPage';
import TaskDetailPage from './pages/crm/TaskDetailPage';
import ActivitiesPage from './pages/crm/ActivitiesPage';
import AiPlannerPage from './pages/crm/AiPlannerPage';
import CrmDashboardPage from './pages/crm/CrmDashboardPage';

import InboxPage from './pages/inbox/InboxPage';
import FlowBuilderPage from './pages/bot/FlowBuilderPage';
import KnowledgeBasePage from './pages/bot/KnowledgeBasePage';
import AgenticFlowsPage from './pages/bot/AgenticFlowsPage';
import SupportTicketsPage from './pages/support/SupportTicketsPage';
import AgentApprovalsPage from './pages/support/AgentApprovalsPage';
import AgentMeterPage from './pages/support/AgentMeterPage';
import AgentHubPage from './pages/agents/AgentHubPage';
import SupportWidgetBuilderPage from './pages/support/SupportWidgetBuilderPage';
import SupportAnalyticsPage from './pages/support/SupportAnalyticsPage';
import SupportApiPlaygroundPage from './pages/support/SupportApiPlaygroundPage';
import ExternalModulesAdminPage from './pages/support/ExternalModulesAdminPage';
import ExternalModulePage from './pages/external/ExternalModulePage';
import McpConnectorsPage from './pages/support/McpConnectorsPage';
import ApiConnectorsPage from './pages/support/ApiConnectorsPage';
import LlmFirewallPage from './pages/support/LlmFirewallPage';
import VoicePage from './pages/voice/VoicePage';
import EnrichPage from './pages/enrich/EnrichPage';
import AdsPage from './pages/ads/AdsPage';
import LinkedInPage from './pages/linkedin/LinkedInPage';
import LinkedInAiPage from './pages/linkedin-ai/LinkedInAiPage';
import XSettingsPage from './pages/social/XSettingsPage';
import ThreadsSettingsPage from './pages/social/ThreadsSettingsPage';
import CrossPostPage from './pages/social/CrossPostPage';
import ContentCalendarPage from './pages/social/ContentCalendarPage';
import WorkflowPage from './pages/workflow/WorkflowPage';
import AnalyticsPage from './pages/analytics/AnalyticsPage';
import BillingPage from './pages/billing/BillingPage';
import IntegrationsPage from './pages/settings/IntegrationsPage';
import CallingAgentPage from './pages/settings/CallingAgentPage';
import StoragePage from './pages/settings/StoragePage';
import CrmMarketingDashboard from './pages/crm/CrmMarketingDashboard';
import LeadFormBuilder from './pages/crm/LeadFormBuilder';
import CrmCampaignsPage from './pages/crm/CrmCampaignsPage';
import CrmFilesPage from './pages/crm/CrmFilesPage';
import CrmTemplatesPage from './pages/crm/CrmTemplatesPage';
import ActivityTemplatesPage from './pages/crm/ActivityTemplatesPage';
import AssignmentRulesPage from './pages/crm/AssignmentRulesPage';
import SharingRulesPage from './pages/crm/SharingRulesPage';
import TaxProfilesPage from './pages/crm/TaxProfilesPage';
import BookkeepingPage from './pages/crm/BookkeepingPage';
import VendorsPage from './pages/crm/VendorsPage';
import PurchaseBillsPage from './pages/crm/PurchaseBillsPage';
import FinanceReportsPage from './pages/crm/FinanceReportsPage';
import FinanceDashboardPage from './pages/crm/FinanceDashboardPage';
import BankingPage from './pages/crm/BankingPage';
import CollectionsPage from './pages/crm/CollectionsPage';
import CompliancePage from './pages/crm/CompliancePage';
import CustomerPortalPage from './pages/CustomerPortalPage';
import ItemsPage from './pages/crm/ItemsPage';
import BudgetsPage from './pages/crm/BudgetsPage';
import FixedAssetsPage from './pages/crm/FixedAssetsPage';
import ExpenseClaimsPage from './pages/crm/ExpenseClaimsPage';
import DeliveryChallansPage from './pages/crm/DeliveryChallansPage';
import RevenueReportPage from './pages/crm/RevenueReportPage';
import SalesDashboardPage from './pages/crm/SalesDashboardPage';
import FinancePage from './pages/crm/FinancePage';
import GstReportPage from './pages/crm/GstReportPage';
import LeadScraperPage from './pages/crm/LeadScraperPage';
import CrmInvoicesPage from './pages/crm/InvoicesPage';
import CrmQuotationsPage from './pages/crm/QuotationsPage';
import RevenueWorkspacePage from './pages/crm/RevenueWorkspacePage';

// Meta WhatsApp Cloud API Pages
import MetaDashboardLayout from './pages/meta/MetaDashboardLayout';
import MetaSettingsPage from './pages/meta/MetaSettingsPage';
import MetaLogsPage from './pages/meta/MetaLogsPage';
import MetaDiagnosticsPage from './pages/meta/MetaDiagnosticsPage';
import MetaTemplatesPage from './pages/meta/MetaTemplatesPage';
import MetaDirectMessagePage from './pages/meta/MetaDirectMessagePage';
import MetaChatPage from './pages/meta/MetaChatPage';
import MetaCampaignsPage from './pages/meta/MetaCampaignsPage';
import MetaAutomationPage from './pages/meta/MetaAutomationPage';
import WhatsAppFlowsPage from './pages/meta/WhatsAppFlowsPage';
import WhatsAppFlowBuilder from './pages/meta/WhatsAppFlowBuilder';
import WhatsAppFlowSessionsPage from './pages/meta/WhatsAppFlowSessionsPage';

// Instagram Workspace Pages
import InstagramDashboardLayout from './pages/instagram/InstagramDashboardLayout';
import InstagramMessagesTab from './pages/instagram/InstagramMessagesTab';
import InstagramCommentsPage from './pages/instagram/InstagramCommentsPage';
import InstagramContentPage from './pages/instagram/InstagramContentPage';
import InstagramSettingsPage from './pages/instagram/InstagramSettingsPage';
import TelegramMessagesTab from './pages/telegram/TelegramMessagesTab';
import TelegramSettingsPage from './pages/telegram/TelegramSettingsPage';
import InstagramAutomationPage from './pages/instagram/InstagramAutomationPage';

// Facebook Workspace Pages
import FacebookDashboardLayout from './pages/facebook/FacebookDashboardLayout';
import FacebookMessagesTab from './pages/facebook/FacebookMessagesTab';
import FacebookCommentsPage from './pages/facebook/FacebookCommentsPage';
import FacebookAutomationPage from './pages/facebook/FacebookAutomationPage';
import FacebookSettingsPage from './pages/facebook/FacebookSettingsPage';

// Meta Ads Workspace Pages
import MetaAdsDashboardLayout from './pages/meta-ads/MetaAdsDashboardLayout';
import MetaAdsCampaignsPage from './pages/meta-ads/MetaAdsCampaignsPage';
import MetaAdsAudiencesPage from './pages/meta-ads/MetaAdsAudiencesPage';
import MetaAdsAnalyticsPage from './pages/meta-ads/MetaAdsAnalyticsPage';
import MetaAdsWalletPage from './pages/meta-ads/MetaAdsWalletPage';
import MetaAdsSettingsPage from './pages/meta-ads/MetaAdsSettingsPage';
import GoogleAdsDashboardLayout from './pages/google-ads/GoogleAdsDashboardLayout';
import GoogleAdsCampaignsPage from './pages/google-ads/GoogleAdsCampaignsPage';
import GoogleAdsAnalyticsPage from './pages/google-ads/GoogleAdsAnalyticsPage';
import GoogleAdsSettingsPage from './pages/google-ads/GoogleAdsSettingsPage';

// Super Admin Pages
import SuperAdminLayout from './pages/superadmin/SuperAdminLayout';
import SuperAdminDashboard from './pages/superadmin/SuperAdminDashboard';
import SuperAdminCompanies from './pages/superadmin/SuperAdminCompanies';
import SuperAdminInvoices from './pages/superadmin/SuperAdminInvoices';
import SuperAdminReconciliation from './pages/superadmin/SuperAdminReconciliation';
import SuperAdminPlans from './pages/superadmin/SuperAdminPlans';
import SuperAdminApiCreditPlans from './pages/superadmin/SuperAdminApiCreditPlans';
import SuperAdminSettings from './pages/superadmin/SuperAdminSettings';
import SuperAdminAPIKeys from './pages/superadmin/SuperAdminAPIKeys';
import SuperAdminPlatformKeys from './pages/superadmin/SuperAdminPlatformKeys';
import SuperAdminEmail from './pages/superadmin/SuperAdminEmail';
import SystemErrors from './pages/superadmin/SystemErrors';
import SuperAdminStorageHealth from './pages/superadmin/SuperAdminStorageHealth';
import SuperAdminPaymentGateways from './pages/superadmin/SuperAdminPaymentGateways';
import SuperAdminPaymentTransactions from './pages/superadmin/SuperAdminPaymentTransactions';
import SuperAdminProfile from './pages/superadmin/SuperAdminProfile';

function App() {
  const dispatch = useDispatch();
  const { isAuthenticated, isSuperAdmin } = useSelector(state => state.auth);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      dispatch(checkAuth());
    }
  }, [dispatch]);

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={isAuthenticated ? <Navigate to={isSuperAdmin ? '/superadmin/dashboard' : '/dashboard'} replace /> : <LoginPage />} />
      <Route path="/accept-team-invitation" element={<AcceptTeamInvitePage />} />
      <Route path="/forms/:id" element={<PublicFormPage />} />
      <Route path="/portal/:token" element={<CustomerPortalPage />} />
      <Route path="/register" element={isAuthenticated ? <Navigate to={isSuperAdmin ? '/superadmin/dashboard' : '/dashboard'} replace /> : <RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />

      {/* User Dashboard Routes — all authenticated users can access their own company dashboard */}
      <Route
        path="/dashboard/*"
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated}>
            <Routes>
              <Route element={<DashboardLayout />}>
                <Route path="phonebook" element={<Navigate to="/dashboard/crm/contacts" replace />} />
                <Route path="subscription" element={<SubscriptionPage />} />
                <Route path="subscription/payment-status" element={<PaymentStatusPage />} />
                <Route path="invoices" element={<InvoicesPage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="settings/email-setup" element={<Navigate to="/dashboard/settings?tab=email" replace />} />
                <Route path="settings/team" element={<TeamManagementPage />} />
                <Route path="settings/channels" element={<Navigate to="/dashboard/settings/integrations" replace />} />
                <Route path="inbox" element={<InboxPage />} />
                <Route path="bot/flows" element={<FlowBuilderPage />} />
                <Route path="bot/agentic-flows" element={<AgenticFlowsPage />} />
                <Route path="bot/knowledge-bases" element={<KnowledgeBasePage />} />
                <Route path="support/widget" element={<SupportWidgetBuilderPage />} />
                <Route path="support/tickets" element={<SupportTicketsPage />} />
                <Route path="support/approvals" element={<AgentApprovalsPage />} />
                <Route path="support/meter" element={<AgentMeterPage />} />
                <Route path="agents" element={<AgentHubPage />} />
                <Route path="support/analytics" element={<SupportAnalyticsPage />} />
                <Route path="support/api-playground" element={<SupportApiPlaygroundPage />} />
                <Route path="support/external-modules" element={<ExternalModulesAdminPage />} />
                <Route path="external/:moduleId" element={<ExternalModulePage />} />
                <Route path="support/mcp-connectors" element={<McpConnectorsPage />} />
                <Route path="support/api-connectors" element={<ApiConnectorsPage />} />
                <Route path="support/llm-firewall" element={<LlmFirewallPage />} />
                <Route path="voice" element={<VoicePage />} />
                <Route path="enrich" element={<EnrichPage />} />
                <Route path="ads" element={<AdsPage />} />
                <Route path="linkedin" element={<LinkedInPage />} />
                <Route path="linkedin/ai" element={<LinkedInAiPage />} />
                <Route path="x" element={<XSettingsPage />} />
                <Route path="threads" element={<ThreadsSettingsPage />} />
                <Route path="social/cross-post" element={<CrossPostPage />} />
                <Route path="social/content-calendar" element={<ContentCalendarPage />} />
                <Route path="workflow" element={<WorkflowPage />} />
                <Route path="analytics" element={<AnalyticsPage />} />
                <Route path="billing-v2" element={<BillingPage />} />
                <Route path="settings/integrations" element={<IntegrationsPage />} />
                <Route path="settings/calling-agent" element={<CallingAgentPage />} />
                <Route path="settings/storage" element={<StoragePage />} />
                <Route path="api-docs" element={<ApiDocsPage />} />
                <Route path="support" element={<SupportPage />} />
                {/* Meta WhatsApp Cloud API Routes */}
                <Route element={<MetaDashboardLayout />}>
                  <Route path="meta" element={<Navigate to="/dashboard/meta/chat" replace />} />
                  <Route path="meta/chat" element={<MetaChatPage />} />
                  <Route path="meta/direct" element={<MetaDirectMessagePage />} />
                  <Route path="meta/campaigns" element={<MetaCampaignsPage />} />
                  <Route path="meta/automation" element={<MetaAutomationPage />} />
                  <Route path="meta/templates" element={<MetaTemplatesPage />} />
                  <Route path="meta/settings" element={<MetaSettingsPage />} />
                  <Route path="meta/logs" element={<MetaLogsPage />} />
                  <Route path="meta/diagnostics" element={<MetaDiagnosticsPage />} />
                  <Route path="whatsapp-flows" element={<WhatsAppFlowsPage />} />
                  <Route path="whatsapp-flows/builder/:flowId" element={<WhatsAppFlowBuilder />} />
                  <Route path="whatsapp-flows/sessions" element={<WhatsAppFlowSessionsPage />} />
                </Route>
                {/* Instagram Workspace Routes */}
                <Route element={<InstagramDashboardLayout />}>
                  <Route path="instagram" element={<Navigate to="/dashboard/instagram/messages" replace />} />
                  <Route path="instagram/messages" element={<InstagramMessagesTab />} />
                  <Route path="instagram/comments" element={<InstagramCommentsPage />} />
                  <Route path="instagram/content" element={<InstagramContentPage />} />
                  <Route path="instagram/automation" element={<InstagramAutomationPage />} />
                  <Route path="instagram/settings" element={<InstagramSettingsPage />} />
                </Route>
                <Route path="telegram" element={<Navigate to="/dashboard/telegram/messages" replace />} />
                <Route path="telegram/messages" element={<TelegramMessagesTab />} />
                <Route path="telegram/settings" element={<TelegramSettingsPage />} />
                {/* Facebook Workspace Routes */}
                <Route element={<FacebookDashboardLayout />}>
                  <Route path="facebook" element={<Navigate to="/dashboard/facebook/messages" replace />} />
                  <Route path="facebook/messages" element={<FacebookMessagesTab />} />
                  <Route path="facebook/comments" element={<FacebookCommentsPage />} />
                  <Route path="facebook/automation" element={<FacebookAutomationPage />} />
                  <Route path="facebook/settings" element={<FacebookSettingsPage />} />
                </Route>
                {/* Meta Ads Workspace Routes */}
                <Route element={<MetaAdsDashboardLayout />}>
                  <Route path="meta-ads" element={<Navigate to="/dashboard/meta-ads/campaigns" replace />} />
                  <Route path="meta-ads/campaigns" element={<MetaAdsCampaignsPage />} />
                  <Route path="meta-ads/audiences" element={<MetaAdsAudiencesPage />} />
                  <Route path="meta-ads/analytics" element={<MetaAdsAnalyticsPage />} />
                  <Route path="meta-ads/wallet" element={<MetaAdsWalletPage />} />
                  <Route path="meta-ads/settings" element={<MetaAdsSettingsPage />} />
                </Route>
                {/* Google Ads Workspace Routes */}
                <Route element={<GoogleAdsDashboardLayout />}>
                  <Route path="google-ads" element={<Navigate to="/dashboard/google-ads/campaigns" replace />} />
                  <Route path="google-ads/campaigns" element={<GoogleAdsCampaignsPage />} />
                  <Route path="google-ads/analytics" element={<GoogleAdsAnalyticsPage />} />
                  <Route path="google-ads/settings" element={<GoogleAdsSettingsPage />} />
                </Route>
                {/* CRM Routes */}
                <Route path="crm" element={<CrmDashboardPage />} />
                <Route path="crm/marketing" element={<CrmMarketingDashboard />} />
                <Route path="crm/marketing/campaigns" element={<CrmCampaignsPage />} />
                <Route path="crm/marketing/forms" element={<LeadFormBuilder />} />
                <Route path="crm/team" element={<TeamManagementPage />} />
                <Route path="crm/departments" element={<DepartmentsPage />} />
                <Route path="crm/files" element={<CrmFilesPage />} />
                <Route path="forms" element={<FormsListPage />} />
                <Route path="forms/:id" element={<FormBuilderPage />} />
                <Route path="forms/:id/submissions" element={<FormSubmissionsPage />} />
                <Route path="crm/templates" element={<CrmTemplatesPage />} />
                <Route path="crm/activity-templates" element={<ActivityTemplatesPage />} />
                <Route path="crm/assignment-rules" element={<AssignmentRulesPage />} />
                <Route path="crm/sharing-rules" element={<SharingRulesPage />} />
                <Route path="crm/tax-profiles" element={<TaxProfilesPage />} />
                <Route path="crm/bookkeeping" element={<BookkeepingPage />} />
                <Route path="crm/vendors" element={<VendorsPage />} />
                <Route path="crm/purchase-bills" element={<PurchaseBillsPage />} />
                <Route path="crm/finance-dashboard" element={<FinanceDashboardPage />} />
                <Route path="crm/banking" element={<BankingPage />} />
                <Route path="crm/collections" element={<CollectionsPage />} />
                <Route path="crm/compliance" element={<CompliancePage />} />
                <Route path="crm/items" element={<ItemsPage />} />
                <Route path="crm/budgets" element={<BudgetsPage />} />
                <Route path="crm/fixed-assets" element={<FixedAssetsPage />} />
                <Route path="crm/expense-claims" element={<ExpenseClaimsPage />} />
                <Route path="crm/delivery-challans" element={<DeliveryChallansPage />} />
                <Route path="crm/finance-reports" element={<FinanceReportsPage />} />
                <Route path="crm/revenue-report" element={<RevenueReportPage />} />
                <Route path="crm/sales-dashboard" element={<SalesDashboardPage />} />
                <Route path="crm/finance" element={<FinancePage />} />
                <Route path="crm/gst-report" element={<GstReportPage />} />
                <Route path="crm/lead-scraper" element={<LeadScraperPage />} />
                <Route path="crm/invoices" element={<CrmInvoicesPage />} />
                <Route path="crm/quotations" element={<CrmQuotationsPage />} />
                <Route path="crm/contacts" element={<ContactsPage />} />
                <Route path="crm/contacts/:contactId" element={<ContactDetailPage />} />
                <Route path="crm/accounts" element={<AccountsPage />} />
                <Route path="crm/accounts/:accountId" element={<AccountDetailPage />} />
                <Route path="crm/opportunities" element={<OpportunitiesPage />} />
                <Route path="crm/events" element={<EventsPage />} />
                <Route path="crm/leads" element={<LeadsPage />} />
                <Route path="crm/leads/:leadId" element={<LeadDetailPage />} />
                <Route path="crm/deals" element={<DealsPage />} />
                <Route path="crm/deals/:dealId" element={<DealDetailPage />} />
                <Route path="crm/activities" element={<ActivitiesPage />} />
                <Route path="crm/tasks" element={<TasksPage />} />
                <Route path="crm/tasks/:taskId" element={<TaskDetailPage />} />
                <Route path="crm/ai-planner" element={<AiPlannerPage />} />
                <Route path="crm/revenue-workspace" element={<RevenueWorkspacePage />} />
                <Route index element={<CrmDashboardPage />} />
                {/* Any unmatched /dashboard/* path (e.g. a stale navigate() target) falls
                    through to a blank page otherwise — there's no catch-all above this. */}
                <Route path="*" element={<Navigate to="/dashboard/crm" replace />} />
              </Route>
            </Routes>
          </ProtectedRoute>
        }
      />

      {/* Super Admin Routes — role: superadmin only */}
      <Route
        path="/superadmin/*"
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated} requireSuperAdmin={true} userIsSuperAdmin={isSuperAdmin}>
            <Routes>
              <Route element={<SuperAdminLayout />}>
                <Route path="dashboard" element={<SuperAdminDashboard />} />
                <Route path="companies" element={<SuperAdminCompanies />} />
                <Route path="invoices" element={<SuperAdminInvoices />} />
                <Route path="reconciliation" element={<SuperAdminReconciliation />} />
                <Route path="plans" element={<SuperAdminPlans />} />
                <Route path="api-credit-plans" element={<SuperAdminApiCreditPlans />} />
                <Route path="settings" element={<SuperAdminSettings />} />
                <Route path="api-keys" element={<SuperAdminAPIKeys />} />
                <Route path="platform-keys" element={<SuperAdminPlatformKeys />} />
                <Route path="email-templates" element={<SuperAdminEmail />} />
                <Route path="coupons" element={<AdminCoupons />} />
                <Route path="system-errors" element={<SystemErrors />} />
                <Route path="storage-health" element={<SuperAdminStorageHealth />} />
                <Route path="payment-gateways" element={<SuperAdminPaymentGateways />} />
                <Route path="payment-transactions" element={<SuperAdminPaymentTransactions />} />
                <Route path="profile" element={<SuperAdminProfile />} />
                <Route index element={<Navigate to="/superadmin/dashboard" replace />} />
              </Route>
            </Routes>
          </ProtectedRoute>
        }
      />

      {/* Landing Page */}
      <Route path="/" element={<LandingPage />} />

      {/* Legal Pages */}
      <Route path="/terms" element={<LegalPage />} />
      <Route path="/privacy" element={<LegalPage />} />
      <Route path="/cookies" element={<LegalPage />} />
      <Route path="/gdpr" element={<LegalPage />} />

      {/* Public Website Pages */}
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/support" element={<WebSupportPage />} />

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
